/* ap-badges.js — Awful Person identity parity: source product-card badges.
 * Copies the Lovable source's two decorative card badges verbatim:
 *   (A) entry ticket badge in the info panel price row (right side)
 *   (B) top-left corner tag (New / Sale / Best Seller) over the image
 * Also hides Horizon's own top-right "Sale" compare-at badge, which the source does not show.
 * Also replaces the hero slideshow's horizontal slide with the source's crossfade.
 * Fully reversible: delete this asset + remove its <script> line in layout/theme.liquid.
 * Map + styling re-derived live from awfulperson.lovable.app on 2026-07-24.
 */
(function () {
  /* 2026-07-30 s52 -- THE TICKET IS NOW DERIVED FROM THE CARD'S OWN PRICE, not from this map.
     WHY: the giveaway app awards entries PER DOLLAR, so a per-ITEM entry count is a promise the
     platform cannot keep. The hardcoded values proved it on the live store -- 'Afro HD Classic
     Tee' and 'Awful Person Snapback' are BOTH $34.99 and carried '+10' and '+5 entries'. Two
     identical prices cannot award different counts. Same correction as the /pages/giveaway body
     (s52); spec is Maya's awfulperson-entry-multiplier-restate.md, on Charles's instruction
     (feed 1785387330 #1).
     DELIBERATE DIVERGENCE from the source, and intended: the source carries the same per-item
     numbers. A newer owner instruction outranks the identity mandate on the entry mechanic.
     FLOOR, not round: "one entry for every $1 spent" means $34.99 -> 34. It is also the
     under-promising direction, so the app awarding 35 costs us nothing.
     `ticket: true` now means only "the source shows a ticket on this card" -- the NUMBER is read
     off the rendered price, so it can never drift from what the customer is charged again.
     COMPACT FORM ('+34', not '34 entries'), and it is the source's own wording on these cards.
     Measured 2026-07-30 at 1024, where the grid first puts the card at 230px: with the word, the
     two SALE cards wrapped the ticket onto two lines jammed against the struck compare-at price
     ('$34.99 $44.99' + '34 entries' overruns the 228px row by 0.29px -- flex shrinks the ticket
     0.16px and the text breaks). '+34' is 41.33px, the same width the old '+10' was, so this
     cannot wrap anywhere the pre-change page did not. The word 'entries' is carried by the ticket
     icon and by /pages/giveaway, which is where the mechanic is actually explained.
     REVERT: restore the literal strings below and drop entriesFromPrice(). */
  /* 2026-08-01 s63 -- ENTRY MULTIPLIER. Charles (Slack 1785554296): "the multipliers are official and
     I need to be able to add 5x,10x, and etc, entries to each and every product". So the ticket is now
     floor(price) x MULTIPLIER, and the multiplier is read off the card's own
     `data-ap-entry-multiplier` attribute, which snippets/product-card.liquid renders from the
     product's `giveaway.entry_multiplier` metafield. Nothing about the rate is stored in this file --
     that is the whole point, and it is the s52 lesson applied one level up: the PRICE comes from the
     card and the RATE comes from the product, so neither can drift from what the customer is charged.
     Shape copied from Gas Monkey Garage on Charles's instruction to check them: their cards carry a
     computed total AND the rate as a separate badge ("6250 entries" + "250X"), rate set per product
     (their hot sauce is 200X where everything else is 250X). Verified against their live cards
     2026-08-01: every one satisfies entries = price x multiplier exactly.
     WHERE THE MULTIPLIER BADGE GOES, and why not next to the ticket: the price row is already
     measured to overflow at a 230px card (see the s28/s52 note above -- the two SALE cards overrun by
     0.29px with one extra word). A second pill in that row would reintroduce exactly that wrap. So it
     sits TOP-RIGHT over the image, a slot that is free because we already hide Horizon's compare-at
     badge there, and it is absolutely positioned so it cannot affect the price row at any width.
     DEFAULT 1 = base rate, and a multiplier of 1 renders NO badge -- so with nothing set (today's
     state) this change is visually a no-op, and the mechanic is inert until Charles sets a number.
     REVERT: drop multiplierOf() + the .ap-mult-tag block and return entriesFromPrice() to its s52
     form. */
  var MAP = {
    'Afro HD Classic Tee':   { ticket: true, corner: null },
    'Street Rider Hoodie':   { ticket: true, corner: null },
    'Moto Snapback':         { ticket: true, corner: null },
    'Chain Link Bracelet':   { ticket: true, corner: null },
    'Awful Person Snapback': { ticket: true, corner: 'New' },
    'Road Glide Tee':        { ticket: true, corner: null },
    'Biker Flannel':         { ticket: true, corner: 'Sale' },
    'Chrome Logo Hoodie':    { ticket: true, corner: 'Best Seller' }
  };

  /* Reads the price the customer is actually charged. Horizon renders the charged price in
     `.price` and the struck compare-at in `.compare-at-price` (no `.price` class), so `.price`
     is the right node on both the sale and the no-sale card. Returns null rather than guessing
     if no money figure is present -- a card with no readable price gets NO ticket, which is the
     safe failure: an absent badge is honest, an invented number is not. */
  function entriesFromPrice(card) {
    var priceEl = card.querySelector('product-price');
    if (!priceEl) return null;
    var node = priceEl.querySelector('.price') || priceEl;
    var m = (node.textContent || '').replace(/,/g, '').match(/\$\s*(\d+(?:\.\d+)?)/);
    if (!m) return null;
    var n = Math.floor(parseFloat(m[1]));
    if (!isFinite(n) || n <= 0) return null;
    return n * multiplierOf(card);
  }

  /* The product's own entries-per-dollar rate, off the attribute product-card.liquid renders from the
     `giveaway.entry_multiplier` metafield. Falls back to 1 -- the base rate -- for anything missing,
     unparseable or below 1, so a card rendered by an older/other template still gets the honest
     "$1 = 1 entry" number rather than no ticket or an inflated one. Integer only: a fractional
     multiplier would make the displayed total disagree with a whole-entry count. */
  function multiplierOf(card) {
    var raw = card.getAttribute('data-ap-entry-multiplier');
    if (raw === null) return 1;
    var n = parseInt(raw, 10);
    return (isFinite(n) && n >= 1) ? n : 1;
  }

  var TICKET_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"' +
    ' fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"' +
    ' stroke-linejoin="round" class="ap-ticket-icon" aria-hidden="true">' +
    '<path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path>' +
    '<path d="M13 5v2"></path><path d="M13 17v2"></path><path d="M13 11v2"></path></svg>';

  var CSS = [
    '.ap-corner-tag{position:absolute;top:12px;left:12px;z-index:3;',
      'background:#f97415;color:#fff;font-size:12px;line-height:16px;font-weight:700;',
      'text-transform:uppercase;padding:4px 8px;border-radius:0;pointer-events:none;',
      'font-family:inherit;letter-spacing:normal;white-space:nowrap;}',
    /* hide Horizon's own top-right compare-at Sale badge on the decorated cards */
    'product-card[data-ap-badged] .product-badges{display:none!important;}',
    /* s63: the entry-multiplier badge, top-RIGHT over the image (the slot the rule above frees).
       Absolutely positioned, so it is outside the price row's flex and cannot cause the 230px
       overflow documented below. Same orange, same weight and the same 12/16 type as the corner
       tag, so the two read as one family when a card carries both. */
    '.ap-mult-tag{position:absolute;top:12px;right:12px;z-index:3;',
      'background:#1a1a1a;color:#f97415;font-size:12px;line-height:16px;font-weight:700;',
      'text-transform:uppercase;padding:4px 8px;border-radius:0;pointer-events:none;',
      'font-family:inherit;letter-spacing:normal;white-space:nowrap;}',
    /* 2026-07-26 s28: the source's price row is `flex items-center
       justify-between` with NO gap, and its entries ticket is `text-xs`
       = 12px/16px in BOTH bands, free to shrink and wrap. Ours had an
       8px gap and a rigid `flex:0 0 auto` ticket on `line-height:1`, so
       on a narrow card (230px at 1024) the ticket overflowed the card
       instead of wrapping, and the row came out 28 against its 32.
       Measured on the source at 1440 / 1024 / 390 -- width-independent,
       hence no breakpoint here. Reversible: gap:8px, flex:0 0 auto,
       line-height:1.
       2026-08-01 s64 CORRECTION to the line above: the source's ticket is NOT
       "free to ... wrap". Its row computes `flex-wrap:nowrap`, so the ticket can
       only shrink, and min-content blocks that. Measured on the source at 390,
       Trending Now band: row 137px, scrollWidth 161px -- the SOURCE OVERFLOWS
       ITS OWN ROW BY 24px, and spills ~6.8px past the card's visible edge.
       Ours spilled 12.8px on the four SALE cards for the same reason: a
       two-price group (119.5px) plus the ticket (41.3px) = 160.8px will not fit
       139px of content box. Not our bug -- inherited from the comp.
       WE DO NOT REPRODUCE A SOURCE BREAK (same call as the 1024-1078 hero
       overflow and the 390 hero CTA; visual-parity-comparison says a broken
       source page is flagged, never copied). `flex-wrap:wrap` lets the ticket
       drop to its own line ONLY when it cannot fit, so every rung where it fits
       is byte-identical to before and nothing source-defined moves: sale price
       18px, compare-at 14px, the 8px intra-price gap and the row's 0/16/16
       padding are all untouched. A large multiplier now grows the card instead
       of spilling out of it.
       Reversible: delete flex-wrap and row-gap. */
    /* row-gap MUST come after `gap:0` -- the shorthand resets both axes. */
    '.ap-price-row{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:0;row-gap:4px;width:100%;}',
    '.ap-price-row > product-price{width:auto!important;--width:auto!important;flex:0 1 auto;margin:0;}',
    '.ap-ticket{display:inline-flex;align-items:center;gap:4px;flex:0 1 auto;',
      'color:#f97415;font-size:12px;font-weight:400;line-height:16px;}',
    '.ap-ticket .ap-ticket-icon{width:12px;height:12px;flex:0 0 auto;}',
    /* Hero: the source's hero band is bg-foreground (#1a1a1a) under its slides;
       Horizon's slideshow-container is white, which shows through mid-transition. */
    '[id*="hero_slideshow"] slideshow-container{background:#1a1a1a;}',
    '.ap-xfade{position:absolute;inset:0;z-index:1;overflow:hidden;pointer-events:none;}',
    '.ap-xfade > slideshow-slide{position:absolute;inset:0;width:100%;height:100%;'+
      'margin:0;flex:none;content-visibility:visible;view-timeline-name:none;pointer-events:none;}'
  ].join('');

  function injectStyle() {
    if (document.getElementById('ap-badges-style')) return;
    var s = document.createElement('style');
    s.id = 'ap-badges-style';
    s.textContent = CSS;
    (document.head || document.documentElement).appendChild(s);
  }

  function decorate(card) {
    if (card.hasAttribute('data-ap-badged')) return;
    var titleEl = card.querySelector('[role="heading"]');
    var title = titleEl ? titleEl.textContent.trim() : '';
    /* s63: a card NOT in the map now still gets a ticket (corner tag: none). Charles's instruction is
       "each and every product", so a product added after this map was written -- an entry pack, a new
       drop -- must carry its entry count without anyone remembering to edit this file. The map now
       only decides the source's New/Sale/Best Seller corner tag, which is genuinely per-product.
       No visible change today: all eight live products are in the map with ticket:true. */
    var data = MAP[title] || { ticket: true, corner: null };
    // s52: only claim the card once the price has actually rendered, otherwise a card hydrated
    // before its price would be marked done and never get its ticket. A count of zero tickets
    // was always going to look identical to "the source shows none here".
    if (data.ticket && entriesFromPrice(card) === null) return;
    card.setAttribute('data-ap-badged', '1');

    // (B) corner tag over the image
    if (data.corner) {
      var gallery = card.querySelector('.card-gallery');
      if (gallery && !gallery.querySelector('.ap-corner-tag')) {
        var tag = document.createElement('div');
        tag.className = 'ap-corner-tag';
        tag.textContent = data.corner;
        gallery.appendChild(tag);
      }
    }

    // (C) s63: the entry-multiplier badge, top-right over the image. Only above the base rate --
    // a "1X" on every card would be noise, and it would also be the first thing to look wrong on
    // the day the rate itself changes.
    var mult = multiplierOf(card);
    if (mult > 1) {
      var gal = card.querySelector('.card-gallery');
      if (gal && !gal.querySelector('.ap-mult-tag')) {
        var mtag = document.createElement('div');
        mtag.className = 'ap-mult-tag';
        mtag.textContent = mult + 'X';
        gal.appendChild(mtag);
      }
    }

    // (A) ticket badge on the right of the price row -- value derived from the price (s52)
    var entries = data.ticket ? entriesFromPrice(card) : null;
    if (entries !== null) {
      var price = card.querySelector('product-price');
      if (price && !card.querySelector('.ap-price-row')) {
        var row = document.createElement('div');
        row.className = 'ap-price-row';
        price.parentNode.insertBefore(row, price);
        row.appendChild(price);
        var ticket = document.createElement('div');
        ticket.className = 'ap-ticket';
        ticket.innerHTML = TICKET_SVG + '<span>+' + entries + '</span>';
        row.appendChild(ticket);
      }
    }
  }

  // Source form-field placeholders (JOIN THE PACK section): Horizon forces a single locale
  // placeholder ("Email address") on both fields; restore the source's own wording.
  // DELIBERATE DIVERGENCE (2026-07-30, s50): the source's second field is a real `type=tel`
  // and says "Enter phone number". OUR build renders Horizon's email-signup block there --
  // `type=email`, `name=contact[email]`, same endpoint and same list as the card above it --
  // so it REJECTS every phone format a visitor who obeys that instruction would type.
  // Matching the source here would mean shipping a tel field that posts nowhere, which is
  // worse than the divergence. So both fields read "Enter your email" until a real SMS app
  // exists (flagged, not bought -- owner directive). Same call as Maya's four copy swaps in
  // templates/index.json (SMS UPDATES -> GIVEAWAY ALERTS etc); those and this move together.
  // Revert path if an SMS app lands: restore the /SMS|PHONE/ context test from git history.
  // Rides the same observer/run() harness so it survives Horizon hydration. Reversible with this file.
  function fixPlaceholders() {
    var inputs = document.querySelectorAll('.email-signup__input');
    for (var i = 0; i < inputs.length; i++) {
      var inp = inputs[i];
      if (inp.getAttribute('placeholder') !== 'Enter your email')
        inp.setAttribute('placeholder', 'Enter your email');
    }
  }

  // X/Twitter footer icon: the Lovable source renders FOUR social icons (facebook,
  // instagram, youtube, twitter) and its twitter anchor is href="#" — an icon that is
  // visible but goes nowhere. Charles is registering the real X account later
  // (2026-07-25, #requests 1784940827); until then the stored URL is a placeholder, so
  // neutralize the anchor to match the source's own inert behaviour rather than send a
  // customer to someone else's account. Delete this function + its run() call to revert.
  function neutralizeXLink() {
    var links = document.querySelectorAll('a[href*="x.com/"], a[href*="twitter.com/"]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      if (a.getAttribute('href') === '#') continue;
      a.setAttribute('href', '#');
      a.removeAttribute('target');
      a.removeAttribute('rel');
      a.addEventListener('click', function (e) { e.preventDefault(); });
    }
  }

  // Header cart glyph: the source uses a lucide shopping-CART (two wheels +
  // a basket path); Horizon ships a shopping-BAG. Swap the glyph in place so
  // the button, its cart-count bubble and its drawer behaviour are untouched.
  // Sizing/colour live in brand-parity.css (`ap-cart-icon`). Reversible:
  // delete this function + its run() call. (2026-07-25)
  var CART_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"' +
    ' fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"' +
    ' stroke-linejoin="round" class="lucide lucide-shopping-cart ap-cart-glyph" aria-hidden="true">' +
    '<circle cx="8" cy="21" r="1"></circle><circle cx="19" cy="21" r="1"></circle>' +
    '<path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path></svg>';

  function fixCartIcon() {
    var icons = document.querySelectorAll('cart-icon');
    for (var i = 0; i < icons.length; i++) {
      var wrap = icons[i].querySelector('.svg-wrapper');
      if (!wrap || wrap.querySelector('.ap-cart-glyph')) continue;
      wrap.innerHTML = CART_SVG;
    }
  }

  // Mobile menu ENTER NOW: the source's inline header panel ends with a
  // full-width gradient ENTER NOW button (358x40, 32px below the last link).
  // Horizon's drawer has no equivalent and no block area to add one, so it is
  // appended to the drawer's <nav>. Geometry/colour live in brand-parity.css
  // (`ap-menu-enter`), which also restyles the drawer into the source's panel.
  // Reversible: delete this function + its run() call. (2026-07-25)
  function addMobileEnterNow() {
    var navs = document.querySelectorAll('.menu-drawer .menu-drawer__navigation');
    for (var i = 0; i < navs.length; i++) {
      if (navs[i].querySelector('.ap-menu-enter')) continue;
      var a = document.createElement('a');
      a.className = 'ap-menu-enter';
      a.href = '/pages/road-glide-giveaway';
      a.textContent = 'Enter Now';
      navs[i].appendChild(a);
    }
  }

  // The source's SHOP nav item is not a collection link — it is the homepage
  // anchor `/#shop`, targeting its SHOP APPAREL section (it has no collection
  // pages at all). Horizon renders that section as `shopify-section-<id>`, so
  // there is no `#shop` to land on. Insert a zero-height anchor at the section's
  // top edge — the source uses no scroll-margin/padding, so the section top
  // lands at y=0 and its own 80px padding clears the 81px fixed header.
  // Matched on heading text, not the section id, so it survives a reorder.
  // Reversible: delete this function + its run() call. (2026-07-26)
  function mountShopAnchor() {
    if (document.getElementById('shop')) return;
    var heads = document.querySelectorAll('h1, h2, h3');
    var sec = null;
    for (var i = 0; i < heads.length; i++) {
      if (/SHOP\s*APPAREL/i.test(heads[i].textContent || '')) {
        sec = heads[i].closest('.shopify-section');
        break;
      }
    }
    if (!sec || !sec.parentNode) return;
    var a = document.createElement('div');
    a.id = 'shop';
    a.setAttribute('aria-hidden', 'true');
    a.style.cssText = 'height:0;line-height:0;font-size:0;';
    sec.parentNode.insertBefore(a, sec);

    // Landing has to be exact: the source puts the section's top edge at the
    // container top (its own 80px inner padding then clears the 81px header).
    // scrollIntoView() stops 92px short here because Chrome refuses to scroll
    // content under the sticky header, and Horizon scrolls .page-wrapper rather
    // than the document — so set the wrapper's scrollTop outright. The source
    // uses scroll-behavior:auto, so this jumps rather than animates.
    function jumpToShop() {
      var wrap = document.querySelector('.page-wrapper');
      var target = document.getElementById('shop');
      if (!wrap || !target) return;
      wrap.scrollTop = target.offsetTop;
    }

    if (location.hash === '#shop') jumpToShop();

    if (!document.documentElement.hasAttribute('data-ap-shop-jump')) {
      document.documentElement.setAttribute('data-ap-shop-jump', '1');
      document.addEventListener('click', function (e) {
        var link = e.target && e.target.closest ? e.target.closest('a') : null;
        if (!link) return;
        var href = link.getAttribute('href') || '';
        if (href !== '/#shop' && href !== '#shop') return;
        if (location.pathname !== '/') return; // let it navigate home first
        e.preventDefault();
        if (location.hash !== '#shop') history.replaceState(null, '', '/#shop');
        jumpToShop();
      });
    }
  }

  // The source has no collection pages at all — every shop-ward link on its
  // homepage (hero CTAs, the series cards, the category cards, Trending's
  // "View All" and Shop Apparel's "View All Products") resolves to /#shop.
  // Horizon generates /collections/<handle> for these from section settings
  // and from auto-linked collection cards, several of which are not settable,
  // so repoint them here. Homepage only: collection pages use /collections/
  // hrefs for pagination and filtering, which must not be rewritten.
  // Reversible: delete this function + its run() call. (2026-07-26)
  function repointShopLinks() {
    var links = [];
    var i;
    if (location.pathname === '/') {
      var main = document.querySelector('main') || document.body;
      var inMain = main.querySelectorAll('a[href*="/collections/"]');
      for (i = 0; i < inMain.length; i++) links.push(inMain[i]);
    }
    // The empty-cart drawer's "Continue shopping" is shared chrome on every
    // route, so it is repointed regardless of which page we are on.
    var drawer = document.querySelectorAll(
      '#shopify-section-cart-drawer-section a[href*="/collections/"], cart-drawer a[href*="/collections/"]'
    );
    for (i = 0; i < drawer.length; i++) links.push(drawer[i]);
    for (i = 0; i < links.length; i++) {
      links[i].setAttribute('href', '/#shop');
    }
  }

  // s31 -- the JOIN THE PACK mascot must be DOWNSAMPLED FROM THE SAME PIXELS as the
  // source's. The source ships one un-srcset 1920x1605 asset and lets the browser
  // shrink it into the 448px box. Horizon's blocks/image.liquid emits
  // widths "240,352,832,1200,..." with sizes:auto, so ours decoded at 448 and the two
  // resamplings differed -- 19,617 differing px at 3% fuzz over the mascot crop (572 at
  // 20%), the whole residue left after the s30 centring + drop-shadow fix.
  // Pinning the SAME 1920 variant makes both browsers run the identical downsample:
  // 2,325 at 3% and 0 at 20%. Measured 2026-07-29 at 1440x900 DPR 1.
  // An intermediate width does NOT help (1200 -> 15,476 at 3%): only an exact match does,
  // which is why this pins 1920 rather than "the biggest available".
  // Gated >=1024 because below that the mascot's group-block is display:none
  // (brand-parity.css), and an un-srcset 1920 PNG is 274KB a phone must not fetch.
  // Reversible: delete this function + its run() call. (2026-07-29)
  function pinMascotResolution() {
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    var sec = document.querySelector('[id*="section_dNKc9B"]');
    if (!sec) return;
    var img = sec.querySelector('.image-block__image');
    if (!img || img.getAttribute('data-ap-pinned')) return;
    var parts = (img.getAttribute('srcset') || '').split(',');
    var url = null;
    for (var i = 0; i < parts.length; i++) {
      var c = parts[i].trim();
      if (/\s1920w$/.test(c)) url = c.split(/\s+/)[0];
    }
    if (!url) return;
    img.setAttribute('data-ap-pinned', '1');
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.removeAttribute('loading');
    img.src = url;
  }

  // The source's header logo decodes its 1920x745 MASTER and lets the browser
  // downsample into the 144.31x56 box. Horizon's `image` snippet hands the browser a
  // server-resized 144x56 (?height=56, plus a 112-tall 2x candidate) instead, so the
  // two sides run different downsamplers over the same artwork. Measured at 390 on
  // 2026-07-29: the logo strip diffed 4,010 at 3% fuzz / 280 at 20% against the
  // source; pinning the master takes it to 117 / 0. It also fixes the box, because
  // the integer server resize rounds the aspect ratio (144.00 -> the source's
  // 144.31). Same step-function-at-equality as pinMascotResolution -- a LARGER
  // variant is not enough, only the source's own decoded size closes it.
  // NOT width-gated: unlike the mascot, the logo paints at every rung, and mobile is
  // the priority inside the identity mandate. Costs ~112KB against the ~40KB variant
  // a DPR-2 phone fetches today -- which is exactly what the source itself ships.
  // Reversible: delete this function + its run() call. (2026-07-29, s34)
  // 2026-07-29 (s38): the FOOTER logo carries the identical defect and was missed in s34.
  // It is a `blocks/image.liquid` brand-image block, so it takes the widths ladder
  // (?width=3840 + srcset) rather than the header's height-keyed path, and at 750 it
  // decoded ?width=1200 against the source's 1920x745 master -- footer diffed 2,880 at
  // 3% fuzz / 271 at 20%, and the residue was the logo alone. Shared chrome, so it taxed
  // every page diff exactly as the header logo did. The block is keyed `logo_brand`, so
  // [class*="__logo_brand"] is a stable hook that survives a re-render (naming the block
  // IS the selector). Stripping `width` yields the master because the CDN returns the
  // master for any width >= its real size.
  function pinLogoResolution() {
    var imgs = document.querySelectorAll('.header-logo__image, [class*="__logo_brand"] .image-block__image');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.getAttribute('data-ap-logo-pinned')) continue;
      var raw = img.getAttribute('src') || '';
      var parts = raw.split('?');
      if (parts.length < 2) continue;
      var kept = [];
      var params = parts[1].split('&');
      for (var j = 0; j < params.length; j++) {
        // drop the CDN resize params, keep the version fingerprint
        if (/^(width|height|crop)=/.test(params[j])) continue;
        kept.push(params[j]);
      }
      img.setAttribute('data-ap-logo-pinned', '1');
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.removeAttribute('loading');
      img.src = parts[0] + (kept.length ? '?' + kept.join('&') : '');
    }
  }

  // The source's hero CROSSFADES between slides (opacity, 0.7s cubic-bezier(.4,0,.2,1),
  // 5s autoplay); Horizon's slideshow scroll-snaps a horizontal track instead. Horizon's
  // whole state machine -- its IntersectionObserver, dots, arrows and autoplay -- keys off
  // that scroller, so we do NOT restyle it. Instead we let it scroll invisibly and paint a
  // crossfade of two frozen clones over the scrollport, which is what the source looks like.
  // Reversible: delete this function + its run() call. (2026-07-26)
  var XFADE_MS = 700;
  var xfadeToken = 0;
  var xfadeBound = false;
  var lastActive = null;
  var xfadeCleanup = null;

  function heroSlideshow() {
    return document.querySelector('[id*="hero_slideshow"] slideshow-component');
  }

  // A clone is inert scenery. It must carry NONE of Horizon's wiring: slides declare
  // ref="slides[]", and Horizon re-scans [ref] on mutation -- leaving those on would
  // register the clones as real slides (refs.slides read 6 instead of 4 mid-fade, which
  // is what drives current/nextIndex/dot state). Strip ref, on:* bindings, ids, slide-id.
  function scrubClone(el) {
    var names = [];
    var i;
    for (i = 0; i < el.attributes.length; i++) {
      var n = el.attributes[i].name;
      if (n === 'ref' || n === 'id' || n === 'slide-id' || n.indexOf('on:') === 0) names.push(n);
    }
    for (i = 0; i < names.length; i++) el.removeAttribute(names[i]);
  }

  function freezeClone(slide) {
    var c = slide.cloneNode(true);
    scrubClone(c);
    var kids = c.querySelectorAll('*');
    for (var i = 0; i < kids.length; i++) scrubClone(kids[i]);
    c.setAttribute('aria-hidden', 'true');
    c.style.transition = 'opacity ' + XFADE_MS + 'ms cubic-bezier(0.4, 0, 0.2, 1)';
    return c;
  }

  function crossfade(comp, from, to) {
    var cont = comp.querySelector('slideshow-container');
    var scroller = comp.querySelector('slideshow-slides');
    if (!cont || !scroller) return;

    // Supersede any fade still running: tear it down rather than orphan its
    // scroll listener and timers, which would otherwise leak one set per change.
    if (xfadeCleanup) xfadeCleanup();
    var stale = cont.querySelectorAll('.ap-xfade');
    for (var i = 0; i < stale.length; i++) stale[i].parentNode.removeChild(stale[i]);
    var token = ++xfadeToken;

    var ov = document.createElement('div');
    ov.className = 'ap-xfade';
    ov.setAttribute('aria-hidden', 'true');
    var a = freezeClone(from);
    var b = freezeClone(to);
    a.style.opacity = '1';
    b.style.opacity = '0';
    ov.appendChild(a);
    ov.appendChild(b);
    cont.appendChild(ov);

    // Hide the real track so its horizontal travel is never painted.
    scroller.style.opacity = '0';
    void ov.offsetWidth; // flush, so the transition has a start value

    a.style.opacity = '0';
    b.style.opacity = '1';

    // Reveal only once BOTH the fade has finished and the native scroll has settled,
    // otherwise the tail of the slide travel shows through.
    var fadeDone = false;
    var scrollIdle = false;
    var revealed = false;
    var idleTimer = null;
    var fadeTimer = null;
    var safetyTimer = null;

    function cleanup() {
      scroller.removeEventListener('scroll', onScroll);
      if (idleTimer) clearTimeout(idleTimer);
      if (fadeTimer) clearTimeout(fadeTimer);
      if (safetyTimer) clearTimeout(safetyTimer);
      if (xfadeCleanup === cleanup) xfadeCleanup = null;
    }
    function reveal() {
      if (revealed || token !== xfadeToken) return;
      revealed = true;
      cleanup();
      scroller.style.opacity = '';
      requestAnimationFrame(function () {
        if (ov.parentNode) ov.parentNode.removeChild(ov);
      });
    }
    function maybeReveal() {
      if (fadeDone && scrollIdle) reveal();
    }
    function onScroll() {
      scrollIdle = false;
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(function () {
        scrollIdle = true;
        maybeReveal();
      }, 120);
    }
    scroller.addEventListener('scroll', onScroll);
    onScroll(); // start the idle clock even if no scroll event ever fires
    xfadeCleanup = cleanup;

    fadeTimer = setTimeout(function () {
      fadeDone = true;
      maybeReveal();
    }, XFADE_MS);
    // Safety net: never leave the real track hidden.
    safetyTimer = setTimeout(function () {
      fadeDone = true;
      scrollIdle = true;
      reveal();
    }, XFADE_MS + 900);
  }

  function heroCrossfade() {
    var comp = heroSlideshow();
    if (!comp || xfadeBound) return;
    var scroller = comp.querySelector('slideshow-slides');
    if (!scroller) return;

    var slides = comp.querySelectorAll('slideshow-slide');
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].getAttribute('aria-hidden') === 'false') lastActive = slides[i];
    }

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // select() stamps aria-hidden="false" on the incoming slide immediately before it
    // starts the scroll, so this fires at exactly the right moment.
    var mo = new MutationObserver(function (records) {
      for (var j = 0; j < records.length; j++) {
        var t = records[j].target;
        if (t.getAttribute('aria-hidden') !== 'false' || t === lastActive) continue;
        var from = lastActive;
        lastActive = t;
        if (from && from !== t && !reduce) crossfade(comp, from, t);
        return;
      }
    });
    mo.observe(scroller, { attributes: true, subtree: true, attributeFilter: ['aria-hidden'] });
    xfadeBound = true;
  }

  // AP PARITY 2026-07-29 (s38): product-card photos are the last non-copy residue on `/`.
  // The source hotlinks unsplash's own ?w=400 file for every product tile; Horizon's
  // srcset ladder hands the browser a ?width=352 Shopify resize of our re-uploaded copy
  // of the SAME photo, so the two browsers downsample from different originals and every
  // tile carries a hollow-outline residue. Measured at 750: TRENDING NOW 16,546 at 3% fuzz
  // / 2,146 at 20%, SHOP APPAREL 21,881 / 59 -- the classic resample ratio, and it survives
  // every geometry check because the boxes already match to the hundredth of a pixel.
  // Only an EXACT source-pixel match closes this (same step function as the mascot, the
  // header logo and the SERIES tiles); a bigger variant buys almost nothing.
  // Keyed by product TITLE, the one stable key on a Horizon card -- NOT the CDN filename,
  // which is ambiguous: `photo-1489987707025-afc232f7ea0f.jpg` backs both a plain ?w=400
  // tile and a ?w=400&h=400&fit=crop one, so a filename-derived rule gets Biker Flannel
  // wrong. The four `fit=crop` entries are the ones the source's Shop Apparel row uses.
  // Reversible: delete this map + function + its run() call. (2026-07-29, s38)
  var AP_CARD_PHOTO = {
    'AFRO HD CLASSIC TEE':   '1521572163474-6864f9cf17ab?w=400',
    'STREET RIDER HOODIE':   '1556821840-3a63f95609a7?w=400',
    'MOTO SNAPBACK':         '1588850561407-ed78c282e89b?w=400',
    'CHAIN LINK BRACELET':   '1611652022419-a9419f74343d?w=400',
    'AWFUL PERSON SNAPBACK': '1588850561407-ed78c282e89b?w=400&h=400&fit=crop',
    'ROAD GLIDE TEE':        '1521572163474-6864f9cf17ab?w=400&h=400&fit=crop',
    'BIKER FLANNEL':         '1489987707025-afc232f7ea0f?w=400&h=400&fit=crop',
    'CHROME LOGO HOODIE':    '1556821840-3a63f95609a7?w=400&h=400&fit=crop'
  };
  function pinCardPhotos() {
    var cards = document.querySelectorAll('product-card');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var heading = card.querySelector('[role="heading"]');
      if (!heading) continue;
      var photo = AP_CARD_PHOTO[heading.textContent.trim().toUpperCase()];
      if (!photo) continue;
      var img = card.querySelector('img');
      if (!img || img.getAttribute('data-ap-photo-pinned')) continue;
      img.setAttribute('data-ap-photo-pinned', '1');
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.removeAttribute('loading');
      img.src = 'https://images.unsplash.com/photo-' + photo;
    }
  }

  /* ap-header-band-880 (2026-07-30 s49) -- the JS half of brand-parity.css's
   * 880-989 band block; read that comment first, the two are ONE fix.
   * Across 880-989 the source shows all five nav links. Horizon's
   * <overflow-list> moves PAST WINNERS into a "More" menu instead, and CSS
   * cannot bring it back: the component sets slot="overflow", and a slotted
   * node is out of CSS's reach. Horizon's own supported lever is the
   * `disabled` attribute -- attributeChangedCallback -> #reset() puts every
   * item back in the row, and :host([disabled]) hides the "More" slot -- so
   * toggle exactly that and nothing else.
   *
   * THE TRAP, and it cost a broken header on one route before I caught it:
   * #reset() does NOT clear the shadow placeholder's inline width, only its
   * height. The placeholder is a real flex item in the list, so on any route
   * whose last reflow HAD overflowed, the list stays as wide as its content
   * plus that stale width -- measured 613.48 instead of 487.78 on
   * /pages/gallery, which threw ENTER NOW 125.71px right and overflowed the
   * header, while /pages/shipping-policy looked perfect. Horizon's showAll()
   * zeroes the placeholder as well as setting `disabled`, but it never undoes
   * that, so re-enabling below 880 or above 989 would leave the placeholder
   * display:none and the list unable to grow. Hence: zero it on the way in,
   * REMOVE the inline props on the way out.
   *
   * Band-scoped deliberately: >=990 the nav already fits and is verified
   * exact, and <880 the "More" collapse is our DELIBERATE divergence (the
   * source's own header breaks there and clips its cart off-screen).
   * Writes only on a state change, so it is safe to call from the
   * MutationObserver in run(). */
  function navBandOverflow() {
    var ol = document.querySelector('#header-component overflow-list.overflow-menu');
    if (!ol || !ol.shadowRoot) return;
    var ph = ol.shadowRoot.querySelector('[part="placeholder"]');
    var inBand = window.innerWidth >= 880 && window.innerWidth <= 989;
    var isOff = ol.getAttribute('disabled') === 'true';

    if (inBand && !isOff) {
      if (ph) {
        ph.style.setProperty('width', '0');
        ph.style.setProperty('display', 'none');
      }
      ol.setAttribute('disabled', 'true');
    } else if (!inBand && isOff) {
      if (ph) {
        ph.style.removeProperty('width');
        ph.style.removeProperty('display');
      }
      ol.removeAttribute('disabled');
    }
  }

  var navBandBound = false;
  var navBandTimer = null;
  function bindNavBandResize() {
    if (navBandBound) return;
    navBandBound = true;
    window.addEventListener('resize', function () {
      clearTimeout(navBandTimer);
      navBandTimer = setTimeout(navBandOverflow, 60);
    });
  }

  function run() {
    injectStyle();
    var cards = document.querySelectorAll('product-card');
    for (var i = 0; i < cards.length; i++) decorate(cards[i]);
    fixPlaceholders();
    neutralizeXLink();
    fixCartIcon();
    addMobileEnterNow();
    mountShopAnchor();
    repointShopLinks();
    pinMascotResolution();
    pinLogoResolution();
    pinCardPhotos();
    heroCrossfade();
    navBandOverflow();
    bindNavBandResize();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Re-apply for any product-cards added after first paint (section rendering, editor).
  var scheduled = false;
  var obs = new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () { scheduled = false; run(); });
  });
  if (document.body) {
    obs.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      obs.observe(document.body, { childList: true, subtree: true });
    });
  }
})();

